import { useEffect, useRef } from 'react';
import { cn } from '@/utils/cn';
import { setupCanvas } from '@/utils/canvasHelpers';
import type { TrailPoint } from '@/hooks/useTrajectoryTrail';
import type { LandmarkPaletteEntry } from '@/hooks/useMultiTrajectoryTrails';
import { dotFillColor } from './trailDotColor';
import {
  drawPhotonTrace,
  prepareRuns,
  PHOTON_TRACE_RUN_POLICY,
  type TraceObservation,
  type TraceRun,
} from './photonTrace';

// ---------------------------------------------------------------------------
// Trail config shape — one entry per landmark trail to draw
// ---------------------------------------------------------------------------

export interface SingleTrailConfig {
  points: TrailPoint[];
  maxVelocity: number;
  /** Palette entry supplying hue + hex for this trail. */
  palette: LandmarkPaletteEntry;
}

// ---------------------------------------------------------------------------
// Ring color (dotFillColor moved to trailDotColor.ts — see import above)
// ---------------------------------------------------------------------------

/**
 * Returns the ring color (luminous halo kept slightly lighter than fill
 * so the dot remains legible on dark video).
 */
export function dotRingColor(hue: number): string {
  return `hsl(${hue}, 80%, 75%)`;
}

// ---------------------------------------------------------------------------
// Rendering constants
// ---------------------------------------------------------------------------

/**
 * Devicepixelratio to request from setupCanvas for this canvas only (capped
 * internally at 3 — see canvasHelpers.ts). Every other overlay canvas keeps
 * drawing at 1x (no global DPR flip; CLAUDE.md-adjacent spec constraint,
 * docs/specs/trace-pointes-ribbon.md C1).
 */
const TRAIL_CANVAS_DPR = typeof window !== 'undefined' ? window.devicePixelRatio || 1 : 1;

/**
 * Dev-only p95 instrumentation (Goal C, step C4 — "measured on a phone").
 * Logs each real draw() tick's wall time to the console, gated on BOTH
 * `import.meta.env.DEV` (never ships to a production build) and the query
 * param `?traildebug=1` (never fires for a coach who didn't ask for it).
 * Does not affect production bundles: Vite dead-code-eliminates the
 * `import.meta.env.DEV` branch in a prod build.
 */
const TRAIL_DEBUG_TIMING_ENABLED =
  import.meta.env.DEV &&
  typeof window !== 'undefined' &&
  new URLSearchParams(window.location.search).get('traildebug') === '1';

/** Radius of each dot in CSS pixels — the backing store is scaled by
 * TRAIL_CANVAS_DPR via setupCanvas's opt-in `{ dpr }`; all drawing in this
 * file stays in CSS px regardless. */
const DOT_RADIUS = 3;

/** Pixel spacing between dot centres along the trail. */
const DOT_SPACING_PX = 9;

/** Width of the luminous ring around each dot. */
const DOT_RING_WIDTH = 1;

/** Alpha for the ring (kept subtle — its job is legibility, not decoration). */
const DOT_RING_ALPHA = 0.55;

/** Leading "pointe" marker: slightly larger filled circle at the head. */
const HEAD_RADIUS = 5;

/**
 * Cap on simultaneous ribbons (owner decision F-4: "cap ribbons at 3, extra
 * selections fall back to dots"). Slots at this index or beyond always draw
 * as dots regardless of `style` — the trailConfigs ARRAY POSITION is used as
 * the slot index (configs already arrive in selection order).
 */
export const MAX_RIBBON_TRAILS = 3;

/**
 * Fixed per-landmark ribbon colour (owner decision C-1: "fixed colour per
 * landmark — no speed→darkness on the ribbon"; speed is read from WIDTH
 * instead, unlike dots mode where speed is read from darkness).
 */

// (PERSIST_MIN_ALPHA removed 2026-07-28: persist mode now renders at constant
// alpha 1 — the age-decay floor made "Persist" read as fading.)

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function clamp(v: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, v));
}

/**
 * Leading "pointe" marker — shared by both dots and ribbon styles so the
 * head treatment (and its ONE shadowBlur use per trail) is byte-identical
 * regardless of which style drew the trail behind it.
 */
export function drawHeadMarker(ctx: CanvasRenderingContext2D, hx: number, hy: number, hue: number): void {
  // Outer glow ring — the only shadowBlur use for this trail.
  ctx.beginPath();
  ctx.arc(hx, hy, HEAD_RADIUS + 2, 0, Math.PI * 2);
  ctx.globalAlpha = 0.35;
  ctx.fillStyle   = dotRingColor(hue);
  ctx.shadowBlur  = 8;
  ctx.shadowColor = dotRingColor(hue);
  ctx.fill();
  ctx.shadowBlur  = 0;
  ctx.shadowColor = 'transparent';

  // Solid head pip
  ctx.beginPath();
  ctx.arc(hx, hy, HEAD_RADIUS, 0, Math.PI * 2);
  ctx.globalAlpha = 1;
  ctx.fillStyle   = `hsl(${hue}, 90%, 65%)`;
  ctx.fill();
}

/**
 * Returns the indices into `points` that fall inside the current visible
 * window — the fade window (or all-history when `persist`), clamped to
 * `[0, curFrame]`, and further clamped to `range` (phase scrub) when given.
 * Extracted verbatim from the component's inline per-trail filter (no
 * behaviour change) so a non-React caller (the ribbon owner-preview
 * builder) can compute the SAME visible-index slice the live renderer
 * feeds into `drawPhotonTrail`/`sampleDotsAlongTrail`.
 */
export function computeVisibleTrailIndices(
  points: TrailPoint[],
  curFrame: number,
  fadeWindow: number,
  persist: boolean,
  range: [number, number] | null,
): number[] {
  const frameStart = persist ? 0 : curFrame - fadeWindow;
  const visibleIndices: number[] = [];
  for (let i = 0; i < points.length; i++) {
    const pt = points[i];
    if (pt.frame > curFrame) continue;
    if (pt.frame < frameStart) continue;
    if (range && (pt.frame < range[0] || pt.frame > range[1])) continue;
    visibleIndices.push(i);
  }
  return visibleIndices;
}

/**
 * Walk a polyline at evenly-spaced arc-length intervals and return the
 * interpolated (x, y, velocityRatio) at each dot position.
 *
 * This gives a TRUE dotted trail regardless of frame rate variation —
 * dots are spaced by screen distance, not by time.
 */
interface DotSample {
  x: number;
  y: number;
  velocityRatio: number;
  alpha: number;
}

/**
 * Letterbox render params for an object-contain video inside a container.
 * Same math as GoalOverlayLayer / UnifiedPoseOverlay: TrailPoint coords are
 * normalized to the VIDEO frame, so when the video aspect differs from the
 * container (portrait athlete clip in a 16:9 panel) the trail must map into
 * the actual video render area, not the full container. (Regression fix
 * 2026-07-29: TrajectoryTrailOverlay was the ONLY overlay without this —
 * traces on aspect-mismatched athlete videos drew at wrong positions.)
 */
export function computeRenderParams(
  videoElement: HTMLVideoElement | null | undefined,
  containerWidth: number,
  containerHeight: number,
): { offsetX: number; offsetY: number; renderWidth: number; renderHeight: number } {
  if (!videoElement || !videoElement.videoWidth || !videoElement.videoHeight) {
    return { offsetX: 0, offsetY: 0, renderWidth: containerWidth, renderHeight: containerHeight };
  }
  const videoAspect = videoElement.videoWidth / videoElement.videoHeight;
  const containerAspect = containerWidth / containerHeight;
  let renderWidth: number;
  let renderHeight: number;
  if (videoAspect > containerAspect) {
    renderWidth = containerWidth;
    renderHeight = containerWidth / videoAspect;
  } else {
    renderHeight = containerHeight;
    renderWidth = containerHeight * videoAspect;
  }
  return {
    offsetX: (containerWidth - renderWidth) / 2,
    offsetY: (containerHeight - renderHeight) / 2,
    renderWidth,
    renderHeight,
  };
}

export function sampleDotsAlongTrail(
  visible: TrailPoint[],
  maxV: number,
  currentFrame: number,
  fadeWindow: number,
  persist: boolean,
  offX: number,
  offY: number,
  rw: number,
  rh: number,
): DotSample[] {
  if (visible.length < 2) return [];

  // Build pixel-space polyline segments from visible trail points.
  // Coordinates map into the video RENDER area (letterbox-compensated).
  type Seg = { x0: number; y0: number; x1: number; y1: number; v0: number; v1: number; f0: number; f1: number };
  const segments: Seg[] = [];
  for (let i = 0; i < visible.length - 1; i++) {
    const a = visible[i];
    const b = visible[i + 1];
    segments.push({
      x0: offX + a.x * rw, y0: offY + a.y * rh,
      x1: offX + b.x * rw, y1: offY + b.y * rh,
      v0: a.velocity, v1: b.velocity,
      f0: a.frame,   f1: b.frame,
    });
  }

  // Walk segments accumulating arc length, emitting a dot every DOT_SPACING_PX.
  const dots: DotSample[] = [];
  let distAccum = 0;
  let nextDotAt  = 0; // first dot at arc-length 0

  for (const seg of segments) {
    const dx = seg.x1 - seg.x0;
    const dy = seg.y1 - seg.y0;
    const segLen = Math.hypot(dx, dy);
    if (segLen < 0.001) continue;

    const ux = dx / segLen;
    const uy = dy / segLen;

    // How far along this segment until the next dot?
    let localT = nextDotAt - distAccum;
    while (localT <= segLen) {
      const frac  = clamp(localT / segLen, 0, 1);
      const velV  = clamp((seg.v0 * (1 - frac) + seg.v1 * frac) / Math.max(maxV, 1), 0, 1);
      const frame = seg.f0 + (seg.f1 - seg.f0) * frac;

      // Opacity: fade based on age — in FADE mode only.
      // Persist mode renders the full trace at constant alpha: "Persist" means
      // the whole trace stays visible (owner regression report 2026-07-28 —
      // the old age-decay-to-0.18 floor made persist read as fading; speed is
      // already encoded via dot darkness, so age-dimming added only confusion).
      const ageFraction = (currentFrame - frame) / Math.max(fadeWindow, 1);
      const alpha = persist ? 1 : clamp(1 - ageFraction, 0, 1);

      if (alpha > 0.01) {
        dots.push({
          x: seg.x0 + ux * localT,
          y: seg.y0 + uy * localT,
          velocityRatio: velV,
          alpha,
        });
      }

      localT       += DOT_SPACING_PX;
      nextDotAt    += DOT_SPACING_PX;
    }
    distAccum += segLen;
  }

  return dots;
}

/**
 * Draws the ring + fill pass for every dot in `dots` (extracted verbatim
 * from the component's inline draw loop so the standalone owner-preview
 * builder can call the exact same per-dot drawing code as the live app,
 * matching the `drawPhotonTrail`/`drawHeadMarker` export pattern above). No
 * behaviour change — same two ctx.arc()/fill() calls per dot, same colour
 * functions, same order.
 */
export function drawDotsTrail(ctx: CanvasRenderingContext2D, dots: DotSample[], hue: number): void {
  for (const dot of dots) {
    // Ring pass (luminous halo)
    ctx.beginPath();
    ctx.arc(dot.x, dot.y, DOT_RADIUS + DOT_RING_WIDTH, 0, Math.PI * 2);
    ctx.globalAlpha = dot.alpha * DOT_RING_ALPHA;
    ctx.fillStyle   = dotRingColor(hue);
    ctx.shadowColor = 'transparent';
    ctx.shadowBlur  = 0;
    ctx.fill();

    // Fill pass (speed→darkness)
    ctx.beginPath();
    ctx.arc(dot.x, dot.y, DOT_RADIUS, 0, Math.PI * 2);
    ctx.globalAlpha = dot.alpha;
    ctx.fillStyle   = dotFillColor(hue, dot.velocityRatio);
    ctx.fill();
  }
}

/**
 * Bridge from this product's frame-indexed trail points to Photon Trace runs.
 *
 * Adapter contract (handoff INTEGRATION.md "Data contract"): `useTrajectoryTrail`
 * has already applied the detector confidence gate (0.5) and the phantom-leap
 * gate, so every point it kept IS a confidence-valid observation (confidence 1
 * here is an explicit adapter choice, not inferred from coordinates). A frame
 * that was dropped is a HOLE in the frame sequence; holes longer than
 * `PHOTON_TRACE_RUN_POLICY.maxBridgedDroppedFrames` split the run so nothing is
 * drawn across unknown movement. Timestamps are true media seconds
 * (`frame / fps`), the same domain as the video element's currentTime.
 *
 * Coordinates are mapped through the viewer's letterbox transform EXACTLY once
 * here (the same `offX + x * rw` the dots pipeline uses).
 */
export function buildPhotonRuns(
  visible: TrailPoint[],
  fps: number,
  offX: number,
  offY: number,
  rw: number,
  rh: number,
): TraceRun[] {
  const safeFps = fps > 0 ? fps : 30;
  const observations: TraceObservation[] = visible.map((pt) => ({
    t: pt.frame / safeFps,
    x: offX + pt.x * rw,
    y: offY + pt.y * rh,
    confidence: 1,
  }));
  // A gap of N dropped frames between kept frames f0 and f1 means f1 - f0 = N + 1.
  const maxGapSeconds = (PHOTON_TRACE_RUN_POLICY.maxBridgedDroppedFrames + 1 + 0.5) / safeFps;
  return prepareRuns(observations, { minConfidence: 0.5, maxGapSeconds });
}

/**
 * Draws one trail in the 'ribbon' style: the approved Photon Trace light
 * treatment (photonTrace.ts) on the SAME visible points, window and display
 * transform the dots pipeline uses. The head light is drawn by the renderer
 * only while a run is active at `mediaTime` (no stale head after a loss).
 */
export function drawPhotonTrail(
  ctx: CanvasRenderingContext2D,
  visible: TrailPoint[],
  fps: number,
  mediaTime: number,
  fadeWindow: number,
  persist: boolean,
  range: [number, number] | null,
  trackHex: string,
  offX: number,
  offY: number,
  rw: number,
  rh: number,
): void {
  const safeFps = fps > 0 ? fps : 30;
  const runs = buildPhotonRuns(visible, safeFps, offX, offY, rw, rh);
  if (runs.length === 0) return;
  // Window start in media seconds — the same policy as the dots' visible
  // window (persist ⇒ from the clip / phase start; else the fade window).
  const clipStart = range ? range[0] / safeFps : 0;
  const startTime = persist ? clipStart : Math.max(clipStart, mediaTime - fadeWindow / safeFps);
  drawPhotonTrace(ctx, runs, {
    time: mediaTime,
    startTime,
    headToleranceSeconds: 1 / safeFps,
    trackHex,
  });
}

// ---------------------------------------------------------------------------
// Idle dirty-check (Goal C / Trace Pointes ribbon, step C1)
//
// Signature of "what would be drawn this tick" — compared against the last
// ACTUALLY drawn signature to skip setupCanvas + all draw work when nothing
// changed. setupCanvas clears the backing store by resetting canvas.width, so
// skipping the call entirely — not just skipping the draw calls after it —
// is what preserves the already-rendered frame on screen. Dirty fields per
// docs/specs/trace-pointes-ribbon.md C1: currentFrame, persistToEnd,
// fadeWindow, phaseRange, width/height, trailConfigs identity (reference
// equality — deliberate; a new array from the parent is treated as a real
// change), videoElement.videoWidth (letterbox metadata loading late is a
// real geometry change). Module-scope (not per-render) — pure function, no
// captures.
// ---------------------------------------------------------------------------

interface DrawSignature {
  mediaTime: number;
  persistToEnd: boolean;
  fadeWindow: number;
  phaseStart: number | null;
  phaseEnd: number | null;
  width: number;
  height: number;
  trailConfigs: SingleTrailConfig[];
  videoWidth: number;
  style: 'dots' | 'ribbon';
}

function signaturesEqual(a: DrawSignature | null, b: DrawSignature): boolean {
  if (!a) return false;
  return (
    a.mediaTime === b.mediaTime &&
    a.persistToEnd === b.persistToEnd &&
    a.fadeWindow === b.fadeWindow &&
    a.phaseStart === b.phaseStart &&
    a.phaseEnd === b.phaseEnd &&
    a.width === b.width &&
    a.height === b.height &&
    a.style === b.style &&
    a.trailConfigs === b.trailConfigs &&
    a.videoWidth === b.videoWidth
  );
}

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface TrajectoryTrailOverlayProps {
  width: number;
  height: number;
  /**
   * Multi-trail configuration.  All trails are drawn in ONE RAF pass.
   * Pass an empty array to render nothing (but keep the canvas mounted).
   */
  trailConfigs: SingleTrailConfig[];
  /**
   * Current frame from the player (fallback time source). When `videoElement`
   * is mounted and has media, the overlay reads the TRUE media time from it
   * every tick instead, so the trail follows presentation time (pause, seek,
   * replay) rather than the player's throttled timeupdate cadence.
   */
  currentFrame: number;
  /** Frames per second of the pose stream — converts frames to media seconds. Defaults to 30. */
  fps?: number;
  /** Number of frames the trail fades over.  Defaults to 30 (~1 s at 30 fps). */
  fadeWindow?: number;
  /** If set, only points within [startFrame, endFrame] are rendered. */
  phaseRange?: [number, number] | null;
  enabled?: boolean;
  /**
   * When true, the trail persists all frames from 0..currentFrame instead of
   * the fadeWindow slice.
   */
  persistToEnd?: boolean;
  /**
   * The underlying <video> element. When provided, trail coordinates are
   * letterbox-compensated to the video's actual render area (object-contain),
   * matching every other overlay. Without it, coordinates map to the full
   * container (correct only when video aspect == container aspect).
   */
  videoElement?: HTMLVideoElement | null;
  /**
   * 'dots' (default): the arc-length-sampled dotted trail, byte-identical to
   * pre-Goal-C behaviour. 'ribbon': the approved Photon Trace light treatment
   * (photonTrace.ts) — only the first MAX_RIBBON_TRAILS (3) entries in
   * `trailConfigs` render as a ribbon; the rest fall back to dots (owner
   * decision F-4). Both styles consume the SAME visible points, window and
   * display transform. The viewer binds this to its Ribbon / Dots control.
   */
  style?: 'dots' | 'ribbon';
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * TrajectoryTrailOverlay
 *
 * Renders velocity-aware trails for up to 5 landmarks in a SINGLE RAF loop,
 * in one of two styles:
 *  - 'dots' (default): arc-length-sampled dotted trail. Each dot's
 *    lightness/saturation is modulated by velocity (speed→darkness: faster =
 *    darker, deeper hue). A thin luminous ring keeps dark dots legible on
 *    video backgrounds.
 *  - 'ribbon': the approved Photon Trace light treatment (photonTrace.ts) —
 *    a narrow continuous light trace in the landmark's palette colour with
 *    an ice-white core, violet-shifted older tail, quiet layered halo, a
 *    tapered beginning and a small leading light; constant CSS-pixel widths
 *    (no speed encoding), capped at MAX_RIBBON_TRAILS simultaneous ribbons
 *    (extra slots draw as dots). See docs/specs/trace-pointes-ribbon.md and
 *    docs/trace-pointes/handoff/.
 *
 * Design decisions (apply to BOTH styles):
 *  - ONE canvas, ONE requestAnimationFrame loop (not N separate instances).
 *    This satisfies the PERF requirement from the cross-review addendum.
 *  - shadowBlur is used ONLY on the dots' head pip (drawHeadMarker) — never
 *    per-dot or per-strip (perf: shadowBlur is the expensive Canvas2D call on
 *    mobile). The ribbon's head light is a small radial gradient instead.
 *  - Both styles read the same visible points (computeVisibleTrailIndices),
 *    the same letterbox transform (computeRenderParams) and the same media
 *    time, so switching style changes rendering only.
 *  - NO Path2D anywhere (absent in jsdom 27, the vitest harness this repo
 *    uses) — every polygon is beginPath()/moveTo/lineTo/fill().
 *
 * Dots-specific history:
 *  - Dotted treatment via arc-length sampling — uniform dot spacing regardless
 *    of frame rate variation.
 *  - 3-pass bloom/glow/core is REMOVED; replaced by dot fill + ring.
 *  - speed→WIDTH is REMOVED; replaced by speed→DARKNESS (hue fixed per
 *    landmark, lightness varies with velocity).
 *  (The ribbon style encodes no speed at all — the handoff rejects width or
 *  darkness modulation; movement geometry alone carries the information.)
 *
 * Coordinate contract:
 *   TrailPoint.x / .y are in [0, 1] normalized video space.  Multiplied by
 *   `width` / `height` to get canvas pixels.
 */
function TrajectoryTrailOverlay({
  width,
  height,
  trailConfigs,
  currentFrame,
  fps = 30,
  fadeWindow = 30,
  phaseRange = null,
  enabled = true,
  persistToEnd = false,
  videoElement = null,
  style = 'dots',
}: TrajectoryTrailOverlayProps) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  // Mutable ref holds latest props so the RAF loop never needs re-registration.
  const stateRef = useRef({
    trailConfigs,
    currentFrame,
    fps,
    fadeWindow,
    phaseRange,
    enabled,
    persistToEnd,
    style,
    width,
    height,
    videoElement,
  });

  stateRef.current = {
    trailConfigs,
    currentFrame,
    fps,
    fadeWindow,
    phaseRange,
    enabled,
    persistToEnd,
    style,
    width,
    height,
    videoElement,
  };

  // ONE RAF loop — registered once, draws ALL trails per tick.
  useEffect(() => {
    let rafId: number;
    let lastDrawnSignature: DrawSignature | null = null;

    function draw() {
      const {
        trailConfigs: configs,
        currentFrame: propFrame,
        fps: streamFps,
        fadeWindow: window,
        phaseRange: range,
        enabled: isEnabled,
        persistToEnd: persist,
        width: w,
        height: h,
        videoElement: vid,
        style: trailStyle,
      } = stateRef.current;

      const canvas = canvasRef.current;
      if (!canvas) {
        rafId = requestAnimationFrame(draw);
        return;
      }

      if (!isEnabled || configs.length === 0) {
        // Nothing to draw — idle tick, no setupCanvas call either.
        rafId = requestAnimationFrame(draw);
        return;
      }

      // True media time: the video element's presentation time when it has
      // media, else the player's frame prop converted to seconds. Both styles
      // derive the integer frame window from the same value, so the visible
      // set of points is identical in Ribbon and Dots.
      const safeFps = streamFps > 0 ? streamFps : 30;
      const mediaTime =
        vid && vid.readyState > 0 && Number.isFinite(vid.currentTime)
          ? vid.currentTime
          : propFrame / safeFps;
      const curFrame = Math.floor(mediaTime * safeFps + 1e-6);
      const signature: DrawSignature = {
        mediaTime,
        persistToEnd: persist,
        fadeWindow: window,
        phaseStart: range ? range[0] : null,
        phaseEnd: range ? range[1] : null,
        width: w,
        height: h,
        trailConfigs: configs,
        videoWidth: vid?.videoWidth ?? 0,
        style: trailStyle,
      };

      if (signaturesEqual(lastDrawnSignature, signature)) {
        // Idle tick: frame and every prop this canvas cares about are
        // unchanged since the last real draw — skip setupCanvas (which would
        // clear the backing store) and every ctx call this tick.
        rafId = requestAnimationFrame(draw);
        return;
      }
      lastDrawnSignature = signature;

      const ctx = setupCanvas(
        canvasRef as React.RefObject<HTMLCanvasElement | null>,
        w,
        h,
        { dpr: TRAIL_CANVAS_DPR },
      );

      if (!ctx) {
        rafId = requestAnimationFrame(draw);
        return;
      }

      const debugStart = TRAIL_DEBUG_TIMING_ENABLED ? performance.now() : 0;

      try {
        // Letterbox mapping — computed per tick (video metadata can load late).
        const { offsetX, offsetY, renderWidth, renderHeight } =
          computeRenderParams(vid, w, h);

        // Draw all trails in this single pass. trailIndex doubles as the
        // "slot" for the ribbon cap (F-4) — trailConfigs already arrives in
        // selection order, so slots 0-2 may ribbon, slots 3-4 always dots.
        for (let trailIndex = 0; trailIndex < configs.length; trailIndex++) {
          const cfg = configs[trailIndex];
          if (cfg.points.length === 0) continue;

          const { hue } = cfg.palette;
          const maxV    = Math.max(cfg.maxVelocity, 1);

          // --- Filter visible trail points (shared by both styles) ---
          // Index-based so the ribbon branch can pull the SAME indices out
          const visibleIndices = computeVisibleTrailIndices(cfg.points, curFrame, window, persist, range);

          if (visibleIndices.length < 2) continue;

          const visible = visibleIndices.map((i) => cfg.points[i]);

          const useRibbon = trailStyle === 'ribbon' && trailIndex < MAX_RIBBON_TRAILS;

          if (useRibbon) {
            drawPhotonTrail(
              ctx, visible, safeFps, mediaTime, window, persist, range, cfg.palette.hex,
              offsetX, offsetY, renderWidth, renderHeight,
            );
            continue;
          }

          // --- Sample dots along the arc-length of the trail ---
          const dots = sampleDotsAlongTrail(
            visible,
            maxV,
            curFrame,
            window,
            persist,
            offsetX,
            offsetY,
            renderWidth,
            renderHeight,
          );

          // --- Draw ring + fill for each dot ---
          drawDotsTrail(ctx, dots, hue);

          // --- Leading "pointe" marker: bright filled circle at the head ---
          const head = visible[visible.length - 1];
          if (head) {
            const hx = offsetX + head.x * renderWidth;
            const hy = offsetY + head.y * renderHeight;
            drawHeadMarker(ctx, hx, hy, hue);
          }
        }

        // Reset composite state to avoid leaking into other overlays
        ctx.globalAlpha = 1;
        ctx.shadowBlur  = 0;
        ctx.shadowColor = 'transparent';
      } catch (err) {
        console.error('[TrajectoryTrailOverlay] Canvas drawing error:', err);
      }

      if (TRAIL_DEBUG_TIMING_ENABLED) {
        const elapsedMs = performance.now() - debugStart;
        // Intentional dev-only instrumentation, gated above (DEV + ?traildebug=1).
        console.log(
          `[TrajectoryTrailOverlay] draw() ${elapsedMs.toFixed(2)}ms — style=${trailStyle}, trails=${configs.length}`,
        );
      }

      rafId = requestAnimationFrame(draw);
    }

    rafId = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(rafId);
  // RAF loop registers once; stateRef keeps it current.
   
  }, []);

  return (
    <canvas
      ref={canvasRef}
      width={width}
      height={height}
      className={cn('absolute inset-0 pointer-events-none', !enabled && 'hidden')}
      style={{ width: '100%', height: '100%' }}
      aria-label="Trace Pointes motion trail overlay"
      aria-hidden="true"
      data-testid="trajectory-trail-overlay"
    />
  );
}

export default TrajectoryTrailOverlay;
